/**
 * /src/charts/bcr-v2/BarChartRaceV2.ts
 * BarChartRaceV2クラスの実装ファイル。
 * Web Components対応版: データのフェッチ・デコードは外部で行い、
 * このクラスは受け取ったオブジェクトの描画に専念する。
 */
import * as d3 from 'd3';
import { Selection, BaseType } from 'd3';
import { BcrDataResult, BcrMappingData } from './api';
import { DataSet as BcrDataSetId } from '../../js/generated/bcr_data_id.pb.js';

type D3Selection<T extends BaseType> = Selection<T, unknown, null, undefined>;

interface BarChartRaceUIState {
  isPlaying: boolean;
  currentUiFrame: number;
  totalUiFrames: number;
  displayDate: string;
}

interface AnimationProgressState {
  isPlaying: boolean;
  currentUiFrame: number;
  progressInFrame: number;
}

type RehydratedFrame = {
  date: string;
  values: BarChartRaceValueItem[];
  isKeyframe: boolean;
  displayDate: string | null;
};

interface BarChartRaceValueItem {
  id: number;
  name: string;
  value: number;
  rank: number;
  fullName: string;
  platforms: string;
  releaseDate: string;
  category: string;
}

interface UIKeyframeMapItem {
  frameIndex: number;
  displayDate: string;
}


export class BarChartRaceV2 {
  //======================================================================
  // 1. プロパティ定義 (クラスが保持する状態)
  //======================================================================

  // --- 基本設定 ---
  private chartElement: HTMLElement;
  public config: any;
  private margin: { top: number; right: number; bottom: number; left: number };

  // --- 描画領域の次元 ---
  private width: number = 0;
  private height: number = 0;
  private lastKnownWidth: number = 0;

  // --- D3.js関連オブジェクト ---
  private svg: D3Selection<SVGSVGElement> | null = null;
  private g: D3Selection<SVGGElement> | null = null;
  private xScale: d3.ScaleLinear<number, number> | null = null;
  private yScale: d3.ScaleBand<string> | null = null;
  private colorScale: d3.ScaleOrdinal<string, string> | null = null;
  private dateText: D3Selection<SVGTextElement> | null = null;
  private xAxisGroup: D3Selection<SVGGElement> | null = null;
  private tooltip: D3Selection<HTMLDivElement> | null = null;

  // --- データとアニメーションの状態 ---
  private decodedData: BcrDataSetId | null = null;
  private mappingData: BcrMappingData | null = null;
  private keyframes: RehydratedFrame[] = [];
  private uiKeyframeMap: UIKeyframeMapItem[] = [];

  public isReady: boolean = false;
  private isPlaying: boolean = false;
  private animationFrameId: number | null = null;
  private totalDenseFrames: number = 0;
  public currentDenseFrame: number = 0;
  private currentUiFrame: number = 0;

  private lastLogicUpdateTime: number = 0;

  private onUpdateUICallback: (state: BarChartRaceUIState) => void = () => { };

  constructor(chartElement: HTMLElement, config = {}) {
    this.chartElement = chartElement;
    const defaultConfig = {
      topN: 10,
      durationPerFrame: 500,
      transitionDuration: 500,
      autoplay: true,
      autoplayDelay: 500,
      barHeight: 50,
      labelFontSize: null,
      dateDisplayFormat: 'year_month',
      dateDisplayFontSize: null,
      initialXMax: 1000000,
      colorBy: 'name',
      xAxisFormatter: d3.format("~s") 
    };
    this.config = Object.assign({}, defaultConfig, config);
    this.margin = { top: 40, right: 10, bottom: 30, left: 50 };
  }

  public async init(data: BcrDataResult): Promise<void> {
    if (!data || !data.decodedData || !data.mappingData) {
      this._displayMessage('有効なデータが渡されませんでした。', 'error');
      return;
    }

    try {
      this.decodedData = data.decodedData;
      this.mappingData = data.mappingData;

      const loadingMessage = this.chartElement.querySelector<HTMLElement>('[data-chart-role="loading"]');
      if (loadingMessage) loadingMessage.style.display = 'none';

      if (this.config.labelFontSize) this.chartElement.style.setProperty('--bar-label-font-size', `${this.config.labelFontSize}px`);
      if (this.config.dateDisplayFontSize) this.chartElement.style.setProperty('--date-display-font-size', `${this.config.dateDisplayFontSize}px`);

      this._processData();
      this.setupDimensions();

      this.lastKnownWidth = this.width;
      this.setupLayers();
      this._initialDrawAxes();
      this.isReady = true;
      this.drawFrame(0, true);
      this.updateUI();

      if (this.config.autoplay === true) {
        setTimeout(() => this.play(), this.config.autoplayDelay);
      }

    } catch (error) {
      this._displayMessage('描画処理中にエラーが発生しました。', 'error');
      console.error("[BarChartRaceV2] Failed to initialize:", error);
      throw error;
    }
  }

  public async update(data: BcrDataResult): Promise<void> {
    this.pause();

    if (!data || !data.decodedData || !data.mappingData) {
      this._displayMessage('更新用の有効なデータが渡されませんでした。', 'error');
      return;
    }

    try {
      this.decodedData = data.decodedData;
      this.mappingData = data.mappingData;

      this._processData();
      this.setupDimensions();
      this.lastKnownWidth = this.width;

      this.setupLayers();
      this._initialDrawAxes();
      this.isReady = true;

      this.currentDenseFrame = 0;
      this.drawFrame(0, true);
      this.updateUI();

      if (this.config.autoplay === true) {
        setTimeout(() => this.play(), this.config.autoplayDelay);
      }

    } catch (error) {
      this._displayMessage('グラフの更新処理中にエラーが発生しました。', 'error');
      console.error("[BarChartRaceV2] Failed to update:", error);
      throw error;
    }
  }

  public clear(): void {
    this.pause();
    this.isReady = false;
    this.svg?.remove();
    this.svg = null;
    this.tooltip?.classed('is-visible', false);
  }

  public play(): void {
    if (this.isPlaying || !this.isReady) return;
    this.isPlaying = true;

    if (this.currentDenseFrame >= this.totalDenseFrames - 1) {
      this.currentDenseFrame = 0;
      this._resetBarsForPlayback();
      this.drawFrame(0, true); 
    }

    this.lastLogicUpdateTime = performance.now();
    this.animationFrameId = requestAnimationFrame(this.animationLoop.bind(this));
    this.updateUI();
  }

  public pause(): void {
    if (!this.isPlaying || this.animationFrameId === null) return;
    this.isPlaying = false;
    cancelAnimationFrame(this.animationFrameId);
    this.animationFrameId = null;
    this.updateUI();
  }

  public togglePlayPause(): void {
    this.isPlaying ? this.pause() : this.play();
  }

  public seek(uiFrameIndex: number): void {
    if (!this.isReady) return;
    const mapIndex = Math.min(Math.round(uiFrameIndex), this.uiKeyframeMap.length - 1);
    const mapItem = this.uiKeyframeMap[mapIndex];
    if (!mapItem) return;

    this.pause();
    this.currentDenseFrame = mapItem.frameIndex;
    this.lastLogicUpdateTime = performance.now();
    this.drawFrame(this.currentDenseFrame, true);
    this.updateUI();
  }

  public getCurrentProgressState(): AnimationProgressState {
    let progressInFrame = 0;
    if (this.isPlaying) {
      const elapsedTime = performance.now() - this.lastLogicUpdateTime;
      progressInFrame = Math.min(1.0, elapsedTime / this.config.durationPerFrame);
    }
    return {
      isPlaying: this.isPlaying,
      currentUiFrame: this.currentUiFrame,
      progressInFrame: progressInFrame,
    };
  }

  public redrawOnResize(): void {
    const containerRect = this.chartElement.getBoundingClientRect();
    const newWidth = containerRect.width - this.margin.left - this.margin.right;

    if (Math.abs(newWidth - this.lastKnownWidth) < 10) return;

    this.lastKnownWidth = newWidth;
    const wasPlaying = this.isPlaying;
    this.pause();
    this.setupDimensions();
    this.setupLayers();
    this._initialDrawAxes();

    const currentUiFrame = this.uiKeyframeMap.findLastIndex(uiFrame => this.currentDenseFrame >= uiFrame.frameIndex);
    this.seek(Math.max(0, currentUiFrame));

    if (wasPlaying) this.play();
  }

  private animationLoop(timestamp: number): void {
    if (!this.isPlaying) return;

    const elapsed = timestamp - this.lastLogicUpdateTime;

    if (elapsed >= this.config.durationPerFrame) {
      this.currentDenseFrame++;
      if (this.currentDenseFrame >= this.totalDenseFrames) {
        this.currentDenseFrame = this.totalDenseFrames - 1;
        this.pause();
        this.updateUI();
        return;
      }
      this.drawFrame(this.currentDenseFrame);
      this.lastLogicUpdateTime += this.config.durationPerFrame;
      this.updateUI();
    }
    this.animationFrameId = requestAnimationFrame(this.animationLoop.bind(this));
  }

  private drawFrame(denseFrameIndex: number, instant = false): void {
    if (!this.xScale || !this.yScale || !this.g || !this.colorScale || !this.xAxisGroup || !this.svg) return;

    const frameData = this.keyframes[denseFrameIndex];
    if (!frameData) return;

    const topNData = frameData.values.slice(0, this.config.topN);
    const maxValue = d3.max(topNData, d => d.value);
    this.xScale.domain([0, maxValue || this.config.initialXMax]);
    this.yScale.domain(topNData.map(d => d.id.toString()));

    const transitionDuration = instant ? 0 : this.config.transitionDuration;
    const t = d3.transition().duration(transitionDuration).ease(d3.easeLinear);

    const xAxis = d3.axisTop(this.xScale).ticks(this.width / 120).tickSize(-this.height - 10).tickFormat(this.config.xAxisFormatter);
    this.xAxisGroup.transition(t).call(xAxis as any);

    const yAxis = d3.axisLeft(this.yScale).tickSize(0);
    this.g.select<SVGGElement>(".y-axis").transition(t).call(yAxis as any);

    const bars = this.g.select<SVGGElement>(".bars-container").selectAll<SVGGElement, BarChartRaceValueItem>('g.bar-group').data(topNData, d => d.id);
    const enterBars = bars.enter().append('g').attr('class', 'bar-group').attr('transform', d => `translate(0, ${this.yScale!(d.id.toString())! + this.height})`);
    enterBars.append('rect').attr('class', 'bar').attr('height', this.yScale.bandwidth()).attr('width', 0);

    const allBars = enterBars.merge(bars);
    allBars
      .on("mouseover", (event, d) => this._handleMouseOver(event, d))
      .on("mousemove", (event) => this._handleMouseMove(event))
      .on("mouseout", () => this._handleMouseOut());

    // 【ドロップアウト処理】
    // 負の値を持つバーには .is-hidden クラスを付与し、CSSで opacity: 0 と pointer-events: none を適用する。
    // これにより、ランキング圏外に落ちたアイテムや除外対象アイテムを視覚的に消去する。
    allBars.classed('is-hidden', d => d.value < 0);

    allBars.transition(t).attr('transform', d => `translate(0, ${this.yScale!(d.id.toString())!})`);

    // 幅の計算: Math.max(0, ...) により、負の値(-1等)を持つ非表示バーは幅0として描画される
    allBars.select<SVGRectElement>('rect.bar').transition(t)
      .attr('width', d => Math.max(0, this.xScale!(d.value)))
      .attr('fill', d => this.colorScale!(this.config.colorBy === 'category' ? d.category : d.name));

    bars.exit().transition(t).attr('transform', `translate(0, ${this.height + 50})`).remove();

    const labels = this.g.select<SVGGElement>(".labels-container").selectAll<SVGGElement, BarChartRaceValueItem>('g.label-group').data(topNData, d => d.id);
    const enterLabels = labels.enter().append('g').attr('class', 'label-group').attr('transform', d => `translate(0, ${this.yScale!(d.id.toString())! + this.height})`);
    enterLabels.append('text').attr('class', 'bar-label-name');
    enterLabels.append('text').attr('class', 'bar-label-value');

    const allLabels = enterLabels.merge(labels);
    allLabels.transition(t).attr('transform', d => `translate(0, ${this.yScale!(d.id.toString())!})`);

    // 【ドロップアウト処理】ラベルも同様に非表示化
    allLabels.classed('is-hidden', d => d.value < 0);

    const flipPoint = 80;
    allLabels.select<SVGTextElement>('text.bar-label-name').attr('y', this.yScale.bandwidth() / 2 - 2).text(d => d.name)
      .transition(t).attr('x', d => this.xScale!(d.value) > flipPoint ? this.xScale!(d.value) - 5 : this.xScale!(d.value) + 5);

    allLabels.select<SVGTextElement>('text.bar-label-value').attr('y', this.yScale.bandwidth() / 2 + 15)
      .transition(t).attr('x', d => this.xScale!(d.value) > flipPoint ? this.xScale!(d.value) - 5 : this.xScale!(d.value) + 5)
      .textTween(function (d) {
        const self = d3.select(this);
        const startValue = +self.text().replace(/,/g, '') || d.value;
        const interpolator = d3.interpolateNumber(startValue, d.value);
        return (time) => d3.format(',.0f')(interpolator(time));
      });

    allLabels.classed('is-inside', d => this.xScale!(d.value) > flipPoint).classed('is-outside', d => this.xScale!(d.value) <= flipPoint);
    labels.exit().transition(t).attr('transform', `translate(0, ${this.height + 50})`).remove();

    if (this.dateText) {
      const currentKeyframe = this.uiKeyframeMap.findLast(ui => denseFrameIndex >= ui.frameIndex);
      if (currentKeyframe) {
        const displayDate = this.getFormattedDate(currentKeyframe.displayDate);
        this.dateText.text(displayDate);
      }
    }
  }

  public onUpdateUI(callback: (state: BarChartRaceUIState) => void): void {
    this.onUpdateUICallback = callback;
  }

  private updateUI(): void {
    if (typeof this.onUpdateUICallback !== 'function' || this.uiKeyframeMap.length === 0) return;

    const currentUiFrameIndex = this.uiKeyframeMap.findLastIndex(
      uiFrame => this.currentDenseFrame >= uiFrame.frameIndex
    );
    const currentUiFrame = Math.max(0, currentUiFrameIndex);

    let progress = 0;
    if (currentUiFrameIndex < this.uiKeyframeMap.length - 1) {
      const startDenseFrame = this.uiKeyframeMap[currentUiFrameIndex].frameIndex;
      const endDenseFrame = this.uiKeyframeMap[currentUiFrameIndex + 1].frameIndex;
      const totalFramesInStep = endDenseFrame - startDenseFrame;
      if (totalFramesInStep > 0) {
        progress = (this.currentDenseFrame - startDenseFrame) / totalFramesInStep;
      }
    }

    const smoothFrame = currentUiFrame + progress;

    const displayDate = this.uiKeyframeMap[currentUiFrame]
      ? this.getFormattedDate(this.uiKeyframeMap[currentUiFrame].displayDate)
      : '';

    this.onUpdateUICallback({
      isPlaying: this.isPlaying,
      currentUiFrame: smoothFrame,
      totalUiFrames: this.uiKeyframeMap.length,
      displayDate: displayDate, // 日付情報を追加
    });
  }

  private _processData(): void {
    if (!this.decodedData || !this.mappingData) return;

    const itemKeyToId: { [key: string]: number } = {};
    Object.values(this.mappingData).forEach(item => { itemKeyToId[item.name] = item.id; });

    let lastMonth = '';
    const frames = this.decodedData.frames || [];

    this.keyframes = frames.filter((frame): frame is { date: string; values: any[] } => typeof frame.date === 'string').map(frame => {
      const currentMonth = frame.date.substring(0, 7);
      const isKeyframe = (currentMonth !== lastMonth);
      lastMonth = currentMonth;

      const valuesWithMeta = frame.values.map(value => {
        const meta = this.mappingData![value.id];
        if (!meta) return null;
        return {
          id: value.id,
          value: value.value || 0,
          rank: value.rank,
          name: meta.name,
          fullName: meta.fullName,
          platforms: meta.platforms,
          releaseDate: meta.releaseDate,
          category: meta.category
        };
      }).filter((v): v is BarChartRaceValueItem => v !== null);

      return {
        date: frame.date,
        isKeyframe: isKeyframe,
        displayDate: isKeyframe ? currentMonth : null,
        values: valuesWithMeta.sort((a, b) => a.rank - b.rank) // ランクでソート
      };
    });
    this.totalDenseFrames = this.keyframes.length;
    this.uiKeyframeMap = this.keyframes.map((frame, index) => ({ frame, index })).filter(item => item.frame.isKeyframe).map(item => ({
      frameIndex: item.index,
      displayDate: item.frame.displayDate!
    }));

    const colorDomainItems = (this.config.colorBy === 'category')
      ? Array.from(new Set(Object.values(this.mappingData).map(item => item.category)))
      : Array.from(new Set(Object.values(this.mappingData).map(item => item.name)));

    this.colorScale = d3.scaleOrdinal(d3.schemeTableau10).domain(colorDomainItems);
  }

  private setupDimensions(): void {
    const containerRect = this.chartElement.getBoundingClientRect();
    this.width = containerRect.width - this.margin.left - this.margin.right;
    this.height = this.config.topN * this.config.barHeight;
  }

  private setupLayers(): void {
    d3.select(this.chartElement).select("svg").remove();
    const wrapper = this.chartElement.closest<HTMLElement>('[data-chart-role="container"]');
    if (wrapper) {
      let tooltip = d3.select(wrapper).select<HTMLDivElement>('.chart-tooltip');
      if (tooltip.empty()) {
        tooltip = d3.select(wrapper).append('div')
          .attr("class", 'chart-tooltip')
          .attr('data-chart-role', 'tooltip');
      }
      this.tooltip = tooltip;
    }

    const totalWidth = this.width + this.margin.left + this.margin.right;
    const totalHeight = this.height + this.margin.top + this.margin.bottom;
    this.svg = d3.select(this.chartElement).append("svg").attr("width", totalWidth).attr("height", totalHeight).attr("viewBox", [0, 0, totalWidth, totalHeight]);
    this.g = this.svg.append("g").attr("transform", `translate(${this.margin.left},${this.margin.top})`);

    this.g.append("g").attr("class", "bars-container");
    this.g.append("g").attr("class", "x-axis").attr("transform", `translate(0, -5)`);
    this.g.append("g").attr("class", "y-axis");
    this.g.append("g").attr("class", "labels-container");
  }

  private _initialDrawAxes(): void {
    if (!this.g || !this.svg || this.keyframes.length === 0) return;

    this.xScale = d3.scaleLinear().range([0, this.width]);
    this.yScale = d3.scaleBand().range([0, this.height]).padding(0.1);

    this.xAxisGroup = this.g.select<SVGGElement>(".x-axis");

    this.dateText = this.svg.append("text").attr("class", "chart-date-display").attr("x", this.width + this.margin.left - 10).attr("y", this.height + this.margin.top + this.margin.bottom - 10);
  }

  private _resetBarsForPlayback(): void {
    if (!this.g) return;
    this.g.select<SVGGElement>(".bars-container").selectAll<SVGGElement, BarChartRaceValueItem>('g.bar-group rect.bar').attr('width', 0);
    this.g.select<SVGGElement>(".labels-container").selectAll('g.label-group').remove();
  }

  private _displayMessage(message: string, type: 'error' | 'warning' = 'error'): void {
    d3.select(this.chartElement).select('[data-chart-role="message"]').remove();
    d3.select(this.chartElement).append('p')
      .attr('class', `chart-message ${type}`)
      .attr('data-chart-role', 'message')
      .text(message);
  }

  private _handleMouseOver(event: MouseEvent, d: BarChartRaceValueItem): void {
    if (!this.tooltip) return;
    const content = `
      <div class="tooltip-name">${d.fullName}</div>
      <div class="tooltip-detail"><span class="label">機種:</span><span>${d.platforms}</span></div>
      <div class="tooltip-detail"><span class="label">発売日:</span><span>${d.releaseDate}</span></div>
    `;
    this.tooltip.html(content).classed("is-visible", true);
  }

  private _handleMouseMove(event: MouseEvent): void {
    if (!this.tooltip) return;
    const wrapper = this.chartElement.closest<HTMLElement>('[data-chart-role="container"]');
    if (!wrapper) return;

    const [x, y] = d3.pointer(event, wrapper);
    const containerWidth = wrapper.offsetWidth;
    const tooltipWidth = this.tooltip.node()?.offsetWidth ?? 0;

    let left = x + 20;
    if (left + tooltipWidth > containerWidth) left = x - tooltipWidth - 20;

    this.tooltip.style("left", `${left}px`).style("top", `${y + 20}px`);
  }

  private _handleMouseOut(): void {
    this.tooltip?.classed("is-visible", false);
  }

  private getFormattedDate(dateStr: string): string {
    if (this.config.dateDisplayFormat === 'year_only') {
      const yearMatch = dateStr.match(/^\d{4}/);
      return yearMatch ? yearMatch[0] : dateStr;
    }
    return dateStr;
  }
}